import { useEffect, useMemo, useState } from 'react';

import {
  invalidatePartBotResultsQuery,
  isValidPartUuid,
  RelatedEntityType,
  TaskResolutionType,
  useGetPartBotResults,
  type PartBotResults,
} from '@frontend/api';
import { TaskPipeline, TaskStatus, TaskResolution } from '@frontend/api';
import { useMemoizedArray } from '@frontend/utils';

import {
  useTaskStatusTracking,
  AutoWatchTasksDescriptor,
  useAutoIsLoading,
} from 'utils/hooks/taskTracking/useTaskStatusTracking';

/**
 * This hook is used to get the part bot results for a part and watches for websocket
 * updates to trigger a refetch.
 * @param partUuid - The uuid of the part to get the part bot results for.
 * @param partFileUuids - The uuids of the part files to get the part bot results for.
 * @returns The part bot results for the part.
 */
export const useLivePartBotResults = ({
  partUuid,
  partFileUuids,
  enabled = true,
}: {
  partUuid?: string;
  partFileUuids: string[];
  enabled?: boolean;
}): {
  partBotResults: PartBotResults | undefined;
  isPartBotResultsFetching: boolean;
} => {
  const memoizedPartFileUuids = useMemoizedArray(partFileUuids);

  const { data: partBotResults, isFetching: isPartBotResultsFetching } =
    useGetPartBotResults(
      { partUuid: partUuid ?? '', partFileUuids: memoizedPartFileUuids },
      {
        queryOptions: {
          enabled:
            enabled &&
            ((memoizedPartFileUuids && memoizedPartFileUuids.length > 0) ||
              isValidPartUuid(partUuid)),
          keepPreviousData: true,
          staleTime: 1000 * 60 * 5, // 5 minutes
        },
      },
    );

  // watch for new websocket messages and monitor loading state
  const { registerAutoWatchCallback, unregisterAutoWatchCallback } =
    useTaskStatusTracking();

  useEffect(() => {
    const descriptor: AutoWatchTasksDescriptor = {
      name: 'part-bot-results',
      sourceEntityType: RelatedEntityType.PART,
      sourceEntityPk: partUuid ?? '',
      pipelines: [TaskPipeline.AI_EXTRACTION],
      statuses: [TaskStatus.COMPLETED],
      resolutions: [TaskResolution.SUCCESS],
    };

    registerAutoWatchCallback({
      callback: () => {
        invalidatePartBotResultsQuery({ partUuid });
      },
      descriptor,
    });
    return () => unregisterAutoWatchCallback(descriptor);
  }, [partUuid, registerAutoWatchCallback, unregisterAutoWatchCallback]);

  return {
    partBotResults,
    isPartBotResultsFetching,
  };
};

export const useIsPartBotResultsLoading = ({
  partUuid,
}: {
  partUuid?: string;
}) => {
  const descriptorProcessing = useMemo(
    () => ({
      name: 'part-bot-processing-results-watcher',
      sourceEntityType: RelatedEntityType.PART,
      sourceEntityPk: partUuid ?? '',
      pipelines: [TaskPipeline.PROCESSING],
    }),
    [partUuid],
  );
  const descriptorAiExtraction = useMemo(
    () => ({
      name: 'part-bot-ai-extraction-results-watcher',
      sourceEntityType: RelatedEntityType.PART,
      sourceEntityPk: partUuid ?? '',
      pipelines: [TaskPipeline.AI_EXTRACTION],
    }),
    [partUuid],
  );

  const isFileProcessingLoading = useAutoIsLoading(descriptorProcessing);
  const isAiExtractionLoading = useAutoIsLoading(descriptorAiExtraction);
  return {
    isFileProcessingLoading,
    isAiExtractionLoading,
    isLoading: isFileProcessingLoading || isAiExtractionLoading,
  };
};

/**
 * Hook that tracks the resolution status of ALL ACTIVE PartBot tasks for a given part UUID.
 *
 * This hook watches for task status updates related to part processing and AI extraction
 * pipelines and returns the resolution status once all tasks related to the part are complete.
 * If there are concurrent PartBot tasks running, this hook will return undefined until all
 * tasks are complete.
 *
 * @param partUuid - The UUID of the part to monitor
 * @returns The current task resolution status (SUCCESS, FAILED, CANCELLED, or undefined)
 */
export const usePartBotTaskQueueResolution = (partUuid?: string | null) => {
  const { registerAutoWatchCallback, unregisterAutoWatchCallback } =
    useTaskStatusTracking();
  const [taskResolution, setTaskResolution] = useState<
    TaskResolutionType | undefined
  >();

  useEffect(() => {
    const descriptor: AutoWatchTasksDescriptor = {
      name: 'part-bot-results-watcher',
      sourceEntityType: RelatedEntityType.PART,
      sourceEntityPk: partUuid ?? '',
      pipelines: [TaskPipeline.PROCESSING, TaskPipeline.AI_EXTRACTION],
      statuses: [TaskStatus.COMPLETED],
      surroundingStatusesPredicate: {
        aggregate: 'all' as const,
        pipelines: [TaskPipeline.PROCESSING, TaskPipeline.AI_EXTRACTION],
        statuses: [TaskStatus.COMPLETED],
      },
    };

    registerAutoWatchCallback({
      callback: ({ updateMessages }) => {
        for (const updateMessage of updateMessages) {
          if (updateMessage.resolution === TaskResolution.SUCCESS) {
            setTaskResolution(TaskResolution.SUCCESS);
            break;
          }
        }
      },
      descriptor,
    });

    return () => {
      unregisterAutoWatchCallback(descriptor);
    };
  }, [partUuid, registerAutoWatchCallback, unregisterAutoWatchCallback]);

  // Effect to handle cleanup
  useEffect(() => {
    if (taskResolution === TaskResolution.SUCCESS) {
      const timeout = setTimeout(() => {
        setTaskResolution(undefined);
      }, 3000);
      return () => clearTimeout(timeout);
    }
  }, [taskResolution]);

  return taskResolution;
};
